import { z } from 'zod';
// ============================================================================
// Common Schemas
// ============================================================================

export const TweetAuthorSchema = z.object({
  id: z.string(),
  name: z.string(),
  username: z.string(),
  profileImageUrl: z.string().url().nullable().optional(),
  verifiedType: z.enum(['none', 'blue', 'business', 'government']).optional(),
  verified: z.boolean().optional(),
});

export const TweetMediaSchema = z.object({
  type: z.enum(['photo', 'video', 'animated_gif']),
  url: z.string().url().optional(),
  previewImageUrl: z.string().url().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
  altText: z.string().optional(),
});

export const TweetSchema = z.object({
  id: z.string(),
  text: z.string(),
  createdAt: z.date(),
  author: TweetAuthorSchema,
  metrics: z
    .object({
      retweetCount: z.number(),
      replyCount: z.number(),
      likeCount: z.number(),
      quoteCount: z.number(),
      bookmarkCount: z.number().optional(),
      impressionCount: z.number().optional(),
    })
    .optional(),
  media: z.array(TweetMediaSchema).optional(),
  referencedTweets: z
    .array(
      z.object({
        type: z.enum(['retweeted', 'quoted', 'replied_to']),
        id: z.string(),
      })
    )
    .optional(),
  entities: z
    .object({
      urls: z
        .array(
          z.object({
            url: z.string().url(),
            expandedUrl: z.string().url(),
            displayUrl: z.string(),
          })
        )
        .optional(),
      hashtags: z.array(z.object({ tag: z.string() })).optional(),
      mentions: z
        .array(
          z.object({
            username: z.string(),
            id: z.string(),
          })
        )
        .optional(),
    })
    .optional(),
});
export type TweetAuthor = z.infer<typeof TweetAuthorSchema>;
export type TweetMedia = z.infer<typeof TweetMediaSchema>;
export type Tweet = z.infer<typeof TweetSchema>;

// ============================================================================
// Likes Schemas
// ============================================================================

export const LikesQuerySchema = z.object({
  limit: z.coerce.number().min(1).max(1000).default(100).optional(),
  cursor: z.coerce.number().min(0).default(0).optional(),
  paginationToken: z.string().optional(),
  sort: z.enum(['recent', 'engagement']).default('recent').optional(),
});

export const LikesResponseSchema = z.object({
  tweets: z.array(TweetSchema),
  nextToken: z.string().optional(),
  totalCount: z.number().optional(),
  cached: z.boolean().optional(),
});

export type LikesQuery = z.infer<typeof LikesQuerySchema>;
export type LikesResponse = z.infer<typeof LikesResponseSchema>;

// ============================================================================
// Bookmarks Schemas
// ============================================================================

export const BookmarksQuerySchema = z.object({
  limit: z.coerce.number().min(1).max(1000).default(100).optional(),
  offset: z.coerce.number().min(0).default(0).optional(),
  pagination_token: z.string().optional(),
  sort: z.enum(['recent', 'engagement']).default('recent').optional(),
});

export const BookmarksResponseSchema = z.object({
  tweets: z.array(TweetSchema),
  nextToken: z.string().optional(),
  totalCount: z.number().optional(),
  cached: z.boolean().optional(),
  timestamp: z.string().optional(),
});

export type BookmarksQuery = z.infer<typeof BookmarksQuerySchema>;
export type BookmarksResponse = z.infer<typeof BookmarksResponseSchema>;

// ============================================================================
// Replies/Mentions Schemas
// ============================================================================

export const ReplySchema = z.object({
  id: z.string(),
  text: z.string(),
  authorId: z.string(),
  author: z.object({
    id: z.string(),
    name: z.string(),
    username: z.string(),
    profileImageUrl: z.string().url().nullable().optional(),
    verifiedType: z.enum(['none', 'blue', 'business', 'government']).optional(),
    verified: z.boolean().optional(),
  }),
  createdAt: z.date().optional(),
  followersCount: z.number(),
  followingCount: z.number(),
  tweetCount: z.number(),
  listedCount: z.number(),
  referencedTweet: z
    .object({
      id: z.string(),
      text: z.string(),
      author: z.object({
        name: z.string().nullable().optional(),
        username: z.string().nullable().optional(),
      }),
    })
    .nullable()
    .optional(),
  isAnswered: z.boolean(),
});
export type Reply = z.infer<typeof ReplySchema>;

export const RepliesQuerySchema = z.object({
  filter: z.enum(['all', 'unanswered']).default('unanswered').optional(),
});

export const RepliesResponseSchema = z.object({
  replies: z.array(ReplySchema),
  unansweredCount: z.number(),
  totalCount: z.number(),
});

export const MarkAnsweredBodySchema = z.object({
  replyId: z.string(),
  action: z.literal('mark_answered'),
});

export const MarkAnsweredResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

export type RepliesQuery = z.infer<typeof RepliesQuerySchema>;
export type RepliesResponse = z.infer<typeof RepliesResponseSchema>;
export type MarkAnsweredBody = z.infer<typeof MarkAnsweredBodySchema>;
export type MarkAnsweredResponse = z.infer<typeof MarkAnsweredResponseSchema>;
